import { CheckCircle2, XCircle, Palmtree, Clock } from "lucide-react";
import type { ComponentType } from "react";

export const WEEK_OFF_LABEL = "Week off";
export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export type AttendanceCategory =
  | "present"
  | "absent"
  | "leave"
  | "holiday"
  | "none";

export function getStatusCategory(status: string): AttendanceCategory {
  if (!status) return "none";
  if (status === "P" || status.startsWith("P/")) return "present";
  if (status === "A") return "absent";
  if (status === "HL" || status === "OH") return "holiday";
  return "leave";
}

export const ATTENDANCE_COLORS = {
  present: {
    dot: "var(--color-success)",
    iconBg: "var(--color-success-light)",
    icon: "var(--color-success-dark)",
    label: "var(--color-success-dark)",
    cellBg: "var(--color-bg-base)",
    cellBorder: "var(--color-border-secondary)",
    timeText: "var(--color-text-secondary)",
  },
  absent: {
    dot: "var(--color-error)",
    iconBg: "var(--color-error-light)",
    icon: "var(--color-error-dark)",
    label: "var(--color-error-dark)",
    cellBg: "var(--color-error-light)",
    cellBorder: "var(--color-attendance-absent-border)",
    timeText: "var(--color-error-dark)",
  },
  leave: {
    dot: "var(--color-primary-600)",
    iconBg: "var(--color-attendance-leave-bg)",
    icon: "var(--color-primary-700)",
    label: "var(--color-primary-700)",
    cellBg: "var(--color-attendance-leave-bg)",
    cellBorder: "var(--color-attendance-leave-border)",
    timeText: "var(--color-primary-700)",
  },
  holiday: {
    dot: undefined as string | undefined,
    iconBg: "var(--color-bg-hover)",
    icon: "var(--color-text-secondary)",
    label: "var(--color-attendance-holiday-text)",
    cellBg: "var(--color-bg-hover)",
    cellBorder: "transparent",
    timeText: "var(--color-attendance-holiday-text)",
  },
  no_data: {
    dot: undefined as string | undefined,
    cellBg: "var(--color-bg-base)",
    cellBorder: "var(--color-border-secondary)",
    timeText: "var(--color-text-tertiary)",
  },
  festival: {
    dot: "var(--color-warning-dark)",
    iconBg: "var(--color-warning-light)",
    icon: "var(--color-warning-dark)",
    label: "var(--color-warning-dark)",
    cellBg: "var(--color-warning-light)",
    cellBorder: "var(--color-attendance-festival-border)",
    timeText: "var(--color-warning-dark)",
  },
} as const;

export function getStatusColors(
  status: string,
  isHoliday?: boolean,
  holidayName?: string | null,
): (typeof ATTENDANCE_COLORS)[keyof typeof ATTENDANCE_COLORS] {
  if (isHoliday) {
    const isFestival = holidayName && holidayName.toLowerCase() !== WEEK_OFF_LABEL.toLowerCase();
    if (isFestival) return ATTENDANCE_COLORS.festival;
    return ATTENDANCE_COLORS.holiday;
  }
  const cat = getStatusCategory(status);
  if (cat === "none") return ATTENDANCE_COLORS.no_data;
  return ATTENDANCE_COLORS[cat];
}

export function getDotColor(status: string): string | undefined {
  if (!status) return undefined;
  const cat = getStatusCategory(status);
  if (cat === "none") return undefined;
  return ATTENDANCE_COLORS[cat].dot;
}

export function getCellStatusText(status: string, holidayName?: string | null): string | null {
  const isFestival = holidayName && holidayName.toLowerCase() !== WEEK_OFF_LABEL.toLowerCase();
  if (isFestival) return "Holiday";
  if (!status || status === "HL" || status === "OH") return null;
  if (status === "A") return "Absent";
  if (getStatusCategory(status) === "leave") return "Leave";
  return null;
}

export interface StatCardConfig {
  key: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  suffix?: string;
}

export const STAT_CARD_CONFIGS: StatCardConfig[] = [
  {
    key: "present",
    label: "Present",
    Icon: CheckCircle2,
    iconBg: ATTENDANCE_COLORS.present.iconBg,
    iconColor: ATTENDANCE_COLORS.present.icon,
  },
  {
    key: "absent",
    label: "Absent",
    Icon: XCircle,
    iconBg: ATTENDANCE_COLORS.absent.iconBg,
    iconColor: ATTENDANCE_COLORS.absent.icon,
  },
  {
    key: "leave",
    label: "On Leave",
    Icon: Palmtree,
    iconBg: ATTENDANCE_COLORS.leave.iconBg,
    iconColor: ATTENDANCE_COLORS.leave.icon,
  },
  {
    key: "avgHours",
    label: "Avg Hours",
    Icon: Clock,
    iconBg: "var(--color-info-light)",
    iconColor: "var(--color-info)",
    suffix: "h",
  },
];

export const LEGEND_ITEMS = [
  { label: "Present", color: ATTENDANCE_COLORS.present.dot },
  { label: "Absent", color: ATTENDANCE_COLORS.absent.dot },
  { label: "Leave", color: ATTENDANCE_COLORS.leave.dot },
  { label: "Holiday", color: ATTENDANCE_COLORS.holiday.dot },
];
